import React, { useState } from "react";
import { modalStyle } from "../../_utils/modalStyle";
import { HighlightOff } from "@mui/icons-material";
import {
  Modal,
  Card,
  Box,
  IconButton,
  CardContent,
  Typography,
  styled,
} from "@mui/material";
import CustomButtonModal from "../common/CustomBtnModal";
import { useTranslation } from "react-i18next";
import useFetch from "../../hooks/useFetch";
import { useSelector } from "react-redux";

const ApplyModal: React.FC<{
  open: boolean;
  close: () => void;
  loading: boolean;
  action: (e?: string | null) => void;
}> = ({ open, close, loading, action }) => {
  const { t } = useTranslation("common");
  const personId = useSelector((state: any) => state.user.person_id);
  const { data, loading: isLoading } = useFetch(
    `people/${personId}/resumes?public=true`
  );

  const count = data?.["hydra:totalItems"] ?? 0;
  const resumes = data?.["hydra:member"] ?? [];

  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (resume: any) => {
    const id = resume["@id"];
    setSelected((current) => (current === id ? null : id));
  };

  const onConfirm = () => {
    action(selected);
  };

  return (
    <Modal open={Boolean(open)}>
      <Card
        sx={{
          ...modalStyle,
          width: { xs: "350px", sm: "445px" },
        }}
      >
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            onClick={close}
            sx={{ ml: "auto", mt: 1, mr: 1 }}
            aria-label="close"
          >
            <HighlightOff />
          </IconButton>
        </Box>
        <CardContent sx={{ pt: 0 }}>
          <Typography sx={{ mt: -4.5 }} textAlign="center" variant="h4">
            {t("offer.action.apply_ask")}
          </Typography>
          {Boolean(open) && <Box>{t("offer.action.apply_text")}</Box>}

          {!isLoading ? (
            <ResumeSection>
              <Typography sx={{ mb: 2 }}>
                {t("resumes.apply_select")}
              </Typography>
              <Grid>
                {resumes?.map((resume: any) => (
                  <ResumeItem
                    key={resume["@id"]}
                    resume={resume}
                    onSelect={handleSelect}
                    selected={selected === resume["@id"]}
                  />
                ))}
              </Grid>
            </ResumeSection>
          ) : (
            <Typography sx={{ mt: 2 }} textAlign="center">
              {t("resumes.loading")}
            </Typography>
          )}

          {count === 0 && !isLoading && (
            <Typography sx={{ mt: 2 }} textAlign="center">
              {t("resumes.no_resume_for_applying")}
            </Typography>
          )}

          <Box
            display="flex"
            alignItems="center"
            width="100%"
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <CustomButtonModal
              secondary
              type="button"
              onClick={close}
              variant="outlined"
              color="secondary"
              text={t(`btn.cancel`)}
            />
            <CustomButtonModal
              onClick={onConfirm}
              type="button"
              variant="contained"
              text={t("btn.confirm")}
              disabled={loading || isLoading}
            />
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default ApplyModal;

const ResumeItem: React.FC<any> = ({ resume, onSelect, selected }) => {
  return (
    <ItemRoot
      className={`${selected ? "selected" : ""}`}
      onClick={() => onSelect(resume)}
    >
      {resume.title}
      <img
        alt="resume"
        src={`/static/resume_templates/${resume.content?.template}.jpg`}
      />
    </ItemRoot>
  );
};

const ItemRoot = styled("div")`
  cursor: pointer;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  img {
    border: 1px solid white;
  }
  &.selected {
    color: ${({ theme }) => theme.palette.primary.main};
    img {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem; /* spacing between items */
`;

const ResumeSection = styled("div")`
  margin-top: 10px;
  text-align: center;
`;
